import { SchemaEntry } from 'services/product-custom-prompt/product-custom-prompt.service';
import { TemplateStatus } from 'services/setup-template/setup-template.model';
import { AuditUser } from 'services/user/user.model';
import { MOCK_PEOPLE } from '../shared/mock-context.const';

/**
 * Dados-semente do Cadastro: versões de prompt do product setup (tela Configurações → Prompts)
 * e templates de configuração do admin (Produtos → Cadastro → Templates).
 */

// ── Prompt Master (fixo, visível só para o admin) ────────────────────────────

export const CADASTRO_PROMPT_MASTER = `Você é um assistente especializado em extração estruturada de dados de documentos trabalhistas brasileiros (fichas de admissão, CTPS, contratos de trabalho, TRCT, holerites e atestados).

Regras gerais:
- Extraia somente informações presentes no documento; nunca invente valores.
- Quando um campo não existir ou estiver ilegível, retorne null.
- Datas no formato ISO 8601 (AAAA-MM-DD). Valores monetários como número decimal em reais, sem símbolo.
- CPF e CNPJ apenas com dígitos. PIS/PASEP com 11 dígitos.
- Responda exclusivamente chamando a tool fornecida, respeitando o JSON Schema.`;

// ── Prompts User por versão ──────────────────────────────────────────────────

const PROMPT_USER_V1 = `Extraia os dados cadastrais do colaborador a partir da ficha de admissão: nome completo, CPF, data de nascimento, data de admissão, cargo e salário base.`;

const PROMPT_USER_V2 = `Extraia os dados cadastrais do colaborador a partir dos documentos admissionais (ficha de admissão, CTPS digital e contrato de trabalho).

Priorize a CTPS digital quando houver divergência de datas. Identifique também o número do PIS/PASEP, a jornada contratual semanal e o tipo de contrato (indeterminado, experiência ou temporário).`;

const PROMPT_USER_V3 = `Extraia os dados cadastrais e contratuais do colaborador a partir dos documentos admissionais e rescisórios (ficha de admissão, CTPS digital, contrato de trabalho e TRCT).

Instruções:
1. Priorize a CTPS digital quando houver divergência de datas de admissão ou desligamento.
2. No TRCT, identifique a causa do afastamento (código e descrição), a data de desligamento, o aviso prévio (trabalhado ou indenizado) e os totais de verbas rescisórias brutas, descontos e líquido.
3. Considere o salário base do contrato; adicionais (insalubridade, periculosidade, noturno) devem ir em "adicionais".
4. Quando o documento for apenas admissional, retorne o bloco "rescisao" como null.
5. Informe em "observacoes" qualquer rasura, campo em branco ou divergência entre documentos.`;

// ── JSON Schemas (input_schema das tools) ───────────────────────────────────

const COLABORADOR_BASE_PROPERTIES = {
	nomeCompleto: { type: 'string', description: 'Nome completo do colaborador' },
	cpf: { type: 'string', description: 'CPF somente com dígitos', minLength: 11, maxLength: 11 },
	dataNascimento: { type: 'string', description: 'Data de nascimento', format: 'date' },
	dataAdmissao: { type: 'string', description: 'Data de admissão', format: 'date' },
	cargo: { type: 'string', description: 'Cargo ou função registrada' },
	salarioBase: { type: 'number', description: 'Salário base mensal em reais', minimum: 0 },
};

const SCHEMA_V1: SchemaEntry[] = [
	{
		name: 'ExtrairFichaAdmissao',
		description: 'Extrai os dados cadastrais básicos da ficha de admissão do colaborador.',
		input_schema: {
			type: 'object',
			properties: COLABORADOR_BASE_PROPERTIES,
			required: ['nomeCompleto', 'cpf', 'dataAdmissao'],
		},
	},
];

const SCHEMA_V2: SchemaEntry[] = [
	{
		name: 'ExtrairDadosAdmissionais',
		description:
			'Extrai dados cadastrais e contratuais dos documentos admissionais do colaborador.',
		input_schema: {
			type: 'object',
			properties: {
				...COLABORADOR_BASE_PROPERTIES,
				pis: { type: 'string', description: 'Número do PIS/PASEP (11 dígitos)' },
				jornadaSemanalHoras: {
					type: 'integer',
					description: 'Jornada contratual semanal em horas',
					minimum: 1,
					maximum: 44,
				},
				tipoContrato: {
					type: 'string',
					description: 'Modalidade do contrato de trabalho',
					enum: ['indeterminado', 'experiencia', 'temporario'],
				},
			},
			required: ['nomeCompleto', 'cpf', 'dataAdmissao', 'tipoContrato'],
		},
	},
];

const SCHEMA_V3: SchemaEntry[] = [
	{
		name: 'ExtrairDadosTrabalhistas',
		description:
			'Extrai dados cadastrais, contratuais e rescisórios do colaborador a partir de documentos de admissão e de rescisão.',
		input_schema: {
			type: 'object',
			properties: {
				...COLABORADOR_BASE_PROPERTIES,
				pis: { type: 'string', description: 'Número do PIS/PASEP (11 dígitos)' },
				jornadaSemanalHoras: {
					type: 'integer',
					description: 'Jornada contratual semanal em horas',
					minimum: 1,
					maximum: 44,
				},
				tipoContrato: {
					type: 'string',
					description: 'Modalidade do contrato de trabalho',
					enum: ['indeterminado', 'experiencia', 'temporario'],
				},
				adicionais: {
					type: 'array',
					description: 'Adicionais salariais habituais',
					items: {
						type: 'object',
						properties: {
							tipo: {
								type: 'string',
								enum: ['insalubridade', 'periculosidade', 'noturno', 'outro'],
							},
							percentual: { type: 'number', minimum: 0, maximum: 100 },
						},
						required: ['tipo'],
					},
				},
				rescisao: {
					type: 'object',
					description: 'Dados do TRCT — null quando o documento for apenas admissional',
					properties: {
						dataDesligamento: { type: 'string', format: 'date' },
						causaAfastamentoCodigo: {
							type: 'string',
							description: 'Código da causa do afastamento (ex.: SJ2)',
						},
						causaAfastamentoDescricao: { type: 'string' },
						avisoPrevio: { type: 'string', enum: ['trabalhado', 'indenizado', 'dispensado'] },
						totalBruto: { type: 'number', minimum: 0 },
						totalDescontos: { type: 'number', minimum: 0 },
						valorLiquido: { type: 'number', minimum: 0 },
					},
					required: ['dataDesligamento', 'causaAfastamentoCodigo'],
				},
				observacoes: {
					type: 'string',
					description: 'Rasuras, campos em branco ou divergências encontradas',
				},
			},
			required: ['nomeCompleto', 'cpf', 'dataAdmissao', 'tipoContrato'],
		},
	},
];

// ── Versões de prompt do product setup ───────────────────────────────────────

export interface CadastroPromptSeed {
	version: number;
	description: string;
	promptUser: string;
	schema: SchemaEntry[];
	isActive: boolean;
	linkedJobsCount: number;
	createdAt: string;
	createdBy: AuditUser;
}

export const CADASTRO_PROMPT_SEEDS: CadastroPromptSeed[] = [
	{
		version: 1,
		description: 'Versão inicial — ficha de admissão',
		promptUser: PROMPT_USER_V1,
		schema: SCHEMA_V1,
		isActive: false,
		linkedJobsCount: 14,
		createdAt: '2026-03-02T13:20:00.000Z',
		createdBy: MOCK_PEOPLE.admin,
	},
	{
		version: 2,
		description: 'Inclui CTPS digital, PIS e tipo de contrato',
		promptUser: PROMPT_USER_V2,
		schema: SCHEMA_V2,
		isActive: false,
		linkedJobsCount: 37,
		createdAt: '2026-05-18T16:45:00.000Z',
		createdBy: MOCK_PEOPLE.gerente,
	},
	{
		version: 3,
		description: 'Admissão e rescisão (TRCT) com adicionais e observações',
		promptUser: PROMPT_USER_V3,
		schema: SCHEMA_V3,
		isActive: true,
		linkedJobsCount: 8,
		createdAt: '2026-08-11T11:05:00.000Z',
		createdBy: MOCK_PEOPLE.gerente,
	},
];

// ── Templates de configuração (admin) ────────────────────────────────────────

/** Id do catálogo Cadastro que a tela de criação envia fixo (`PRODUCT_CATALOG_ID`) */
export const CADASTRO_CREATE_PAGE_CATALOG_ID = '019440f7-fc00-7200-8000-000000000001';

export interface TemplateVersionSeed {
	version: number;
	status: TemplateStatus;
	description: string;
	promptUser: string;
	schema: SchemaEntry[];
	createdAt: string;
	createdBy: AuditUser;
	updatedAt: string;
	publishedAt: string | null;
	archivedAt: string | null;
}

export interface TemplateGroupSeed {
	id: string;
	/** slug de MOCK_VERTICAL_INFO */
	verticalSlug: string;
	/** id de MOCK_BRANCHES (mocks/shared/prototype-clients.const) */
	branchId: string | null;
	versions: TemplateVersionSeed[];
}

export const CADASTRO_TEMPLATE_SEEDS: TemplateGroupSeed[] = [
	{
		id: 'tpl-cad-trab',
		verticalSlug: 'trabalhista',
		branchId: null,
		versions: [
			{
				version: 1,
				status: TemplateStatus.ARCHIVED,
				description: 'Ficha de admissão — padrão',
				promptUser: PROMPT_USER_V1,
				schema: SCHEMA_V1,
				createdAt: '2026-02-10T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-05-20T12:00:00.000Z',
				publishedAt: '2026-02-12T12:00:00.000Z',
				archivedAt: '2026-05-20T12:00:00.000Z',
			},
			{
				version: 2,
				status: TemplateStatus.PUBLISHED,
				description: 'Documentos admissionais — CTPS, contrato e ficha',
				promptUser: PROMPT_USER_V2,
				schema: SCHEMA_V2,
				createdAt: '2026-05-15T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-05-20T12:00:00.000Z',
				publishedAt: '2026-05-20T12:00:00.000Z',
				archivedAt: null,
			},
			{
				version: 3,
				status: TemplateStatus.DRAFT,
				description: 'Admissão e rescisão (TRCT)',
				promptUser: PROMPT_USER_V3,
				schema: SCHEMA_V3,
				createdAt: '2026-09-02T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-09-18T12:00:00.000Z',
				publishedAt: null,
				archivedAt: null,
			},
		],
	},
	{
		id: 'tpl-cad-trab-aviacao',
		verticalSlug: 'trabalhista',
		branchId: '1',
		versions: [
			{
				version: 1,
				status: TemplateStatus.PUBLISHED,
				description: 'Admissão de aeronautas — licença ANAC e CMA',
				promptUser: `${PROMPT_USER_V2}\n\nPara aeronautas, extraia também o código ANAC, a validade do Certificado Médico Aeronáutico (CMA) e a base contratual.`,
				schema: SCHEMA_V2,
				createdAt: '2026-06-03T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-06-05T12:00:00.000Z',
				publishedAt: '2026-06-05T12:00:00.000Z',
				archivedAt: null,
			},
		],
	},
	{
		id: 'tpl-cad-trib-financeiro',
		verticalSlug: 'tributario',
		branchId: '3',
		versions: [
			{
				version: 1,
				status: TemplateStatus.PUBLISHED,
				description: 'Informes de rendimentos e holerites',
				promptUser:
					'Extraia do informe de rendimentos e dos holerites: CNPJ da fonte pagadora, CPF do beneficiário, rendimentos tributáveis, IRRF retido, contribuição previdenciária e 13º salário.',
				schema: SCHEMA_V1,
				createdAt: '2026-04-08T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-04-09T12:00:00.000Z',
				publishedAt: '2026-04-09T12:00:00.000Z',
				archivedAt: null,
			},
			{
				version: 2,
				status: TemplateStatus.TESTING,
				description: 'Inclui PLR e dedução de dependentes',
				promptUser:
					'Extraia do informe de rendimentos e dos holerites: CNPJ da fonte pagadora, CPF do beneficiário, rendimentos tributáveis, IRRF retido, contribuição previdenciária, 13º salário, PLR e quantidade de dependentes declarados.',
				schema: SCHEMA_V1,
				createdAt: '2026-08-25T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-09-01T12:00:00.000Z',
				publishedAt: null,
				archivedAt: null,
			},
		],
	},
	{
		id: 'tpl-cad-civel',
		verticalSlug: 'civel',
		branchId: null,
		versions: [
			{
				version: 1,
				status: TemplateStatus.DRAFT,
				description: 'Contratos de prestação de serviço',
				promptUser:
					'Extraia do contrato de prestação de serviços: partes (nome e CPF/CNPJ), objeto, valor, forma de pagamento, vigência e foro de eleição.',
				schema: SCHEMA_V1,
				createdAt: '2026-09-12T12:00:00.000Z',
				createdBy: MOCK_PEOPLE.admin,
				updatedAt: '2026-09-12T12:00:00.000Z',
				publishedAt: null,
				archivedAt: null,
			},
		],
	},
];
